import { Observable, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import {
  ViewIds,
  type APIReferenceType,
  type APIReferenceAttributes,
} from '@ardoq/api-types';
import { viewSettingsConsts } from '@ardoq/view-settings';
import {
  HasViewInstanceId,
  ParentChildGraphEdge,
  type RelationshipDiagramViewModelStreamState,
  type RelationshipsLink,
  type RelationshipsViewSettings,
  RelationshipsViewStreamedProps,
  isParentChildGraphEdge,
  relationshipsViewDistinctReferenceKey,
  resetRelationshipsView,
} from '@ardoq/graph';
import { WorkspaceModeInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import { getWorkspaceModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import {
  action$,
  combineReducers,
  dispatchAction,
  streamReducer,
  withNamespace,
} from '@ardoq/rxbeach';
import { notifyViewLoading } from 'tabview/actions';
import { componentsChanged$ } from 'streams/components/components$';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { buildViewModel } from 'tabview/relationshipDiagrams/viewModel$/buildViewModel';
import { DATA_LIMIT_CHECK_DISABLED } from 'tabview/relationshipDiagrams/consts';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import {
  getComponentIdsFromViewModel,
  getDefaultRelationshipDiagramStateForWorkspaceMode,
  overrideRelationshipsTraverseOptions,
} from 'tabview/relationshipDiagrams/utils';
import { getFilteredViewSettings$ } from 'views/filteredViewSettings$';
import { globalViewModelResetSource$ } from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import { handleComponentsUpdate } from 'tabview/relationshipDiagrams/viewModel$/handleComponentUpdate';
import { GlobalRelationshipsViewModelResetStreamShape } from 'tabview/relationshipDiagrams/viewModel$/types';
import { getDefaultRelationshipDiagramsViewInterface } from 'tabview/relationshipDiagrams/viewModel$/getDefaultRelationshipDiagramsViewInterface';
import { scopeDiff$ } from 'scope/scopeDiff$';
import { isTruthy, setStateProperty } from '@ardoq/common-helpers';
import { orgUsersInterface } from 'modelInterface/orgUsers/orgUsersInterface';
import { filterInterface } from '@ardoq/filter-interface';
import { modelOperations } from '@ardoq/core';
import { getReferenceTypesWithDefaults } from '@ardoq/data-model';
import { VIRTUAL_MODEL_ID } from '@ardoq/global-consts';
import { references$ } from 'streams/references/references$';
import { models$, type ModelsState } from 'streams/models/models$';
import { type StreamCollection } from 'streams/utils/streamUtils';
import { uniqWith, isEqual } from 'lodash';
import { i18nLocale$ } from 'streams/i18nLocale$';
import type { I18nLocale } from '@ardoq/i18n';
import {
  getErrorInfoBoxLabels,
  type ErrorInfoBoxLabels,
} from 'locales/getErrorInfoBoxLabels';

const VIEW_SETTINGS_TO_IGNORE_IN_SOURCE_DATA: (keyof RelationshipsViewSettings)[] =
  ['nodeDistance', 'collapsedGroupIds', viewSettingsConsts.IS_LEGEND_ACTIVE];

const VIEW_ID = ViewIds.NETWORK;

const isParentChildLink = (link: RelationshipsLink) =>
  isParentChildGraphEdge(link.edge);

const resolveReferenceType = (
  models: ModelsState,
  reference: APIReferenceAttributes
): APIReferenceType | null => {
  const model = models.byId[reference.model];
  if (!model) return null;
  if (reference.model === VIRTUAL_MODEL_ID) {
    return model.referenceTypes[reference.type] ?? null;
  }
  return modelOperations.getModelType(
    getReferenceTypesWithDefaults(model),
    reference.type
  );
};

const getReferenceTypesForLegend = (
  links: RelationshipsLink[],
  references: StreamCollection<APIReferenceAttributes>,
  models: ModelsState
): APIReferenceType[] => {
  const hasParentChildReferences = links.some(isParentChildLink);

  const referenceTypes = links
    .filter(link => !isParentChildLink(link))
    .map(link => references.byId[link.edge.modelId])
    .filter(isTruthy)
    .map(reference => resolveReferenceType(models, reference))
    .filter(isTruthy);

  return [
    ...uniqWith(referenceTypes, isEqual),
    ...(hasParentChildReferences ? [ParentChildGraphEdge.getType()] : []),
  ];
};

type ViewModelResetStreamShape =
  GlobalRelationshipsViewModelResetStreamShape & {
    viewSettings: RelationshipsViewSettings;
  };

type SourceDataShape =
  RelationshipDiagramViewModelStreamState<RelationshipsViewSettings>;

/**
 * Sets the right args for buildViewModel and creates the source data shape
 */
const resetReducer = (
  previousState: SourceDataShape,
  {
    context,
    components,
    scenarioRelatedComponentIds,
    graphModel,
    scenario,
    loadedGraph,
    viewSettings,
    i18nLocale,
  }: ViewModelResetStreamShape
): SourceDataShape => {
  const { viewInstanceId, viewInterface, scopeDiffState } = previousState;

  const [loadedScenarioData, scenarioRelatedComponents, { showScopeRelated }] =
    scenario;

  const { rawGraph, nodeLabelsData, ...viewModel } = buildViewModel({
    viewInterface,
    context,
    components,
    scenarioRelatedComponentIds,
    viewSettings,
    graphModel,
    loadedScenarioData,
    scenarioRelatedComponents,
    isInScopeDiffMode: Boolean(scopeDiffState.scopeDiff),
    scopeDiff: scopeDiffState.scopeDiff,
    expandedStateChangingGroupId: null,
    showScopeRelated,
    overrideTraverseOptions: overrideRelationshipsTraverseOptions,
    getDistinctReferenceKey: relationshipsViewDistinctReferenceKey,
    i18nLocale,
    shouldCollapseGraph: true,
    viewId: VIEW_ID,
    dataLimitCheck: DATA_LIMIT_CHECK_DISABLED,
  });

  return {
    ...previousState,
    viewModel: {
      ...viewModel,
      nodeLabelsData,
    },
    viewSettings,
    streamState: {
      context,
      components,
      scenarioRelatedComponentIds,
      graphModel,
      loadedScenarioData,
      scenarioRelatedComponents,
      showScopeRelated,
      skipRender: false, // To be removed, we filter the view settings on the source stream level
      isUpdatingAppState: false, // To be removed, we don't emit source data updates when the app state updates
      rawGraph,
      getDistinctReferenceKey: relationshipsViewDistinctReferenceKey,
      performanceTrackingId: null,
    },
    viewInstanceId,
    noContext: false,
    loadedGraph,
    viewId: VIEW_ID,
  };
};

const toIsLegendActive = ({
  currentSettings,
}: WorkspaceModeInstanceViewSettings<ViewIds.NETWORK>): boolean =>
  currentSettings.isLegendActive;

const withIsLegendActive = <
  Props extends { viewSettings: RelationshipsViewSettings },
>([props, isLegendActive]: [Props, boolean]): Props => ({
  ...props,
  viewSettings: { ...props.viewSettings, isLegendActive },
});

export const withLiveIsLegendActive = <
  Props extends { viewSettings: RelationshipsViewSettings },
>(
  props$: Observable<Props>,
  viewSettingsWithChanges$: Observable<
    WorkspaceModeInstanceViewSettings<ViewIds.NETWORK>
  >
): Observable<Props> =>
  combineLatest([
    props$,
    viewSettingsWithChanges$.pipe(
      map(toIsLegendActive),
      distinctUntilChanged()
    ),
  ]).pipe(map(withIsLegendActive));

export const getViewModel$ = ({ viewInstanceId }: HasViewInstanceId) => {
  const viewSettingsWithChanges$ = getWorkspaceModeViewSettingsWithChanges$(
    VIEW_ID,
    viewInstanceId
  );
  const filteredViewSettings$ = getFilteredViewSettings$(
    viewSettingsWithChanges$,
    VIEW_SETTINGS_TO_IGNORE_IN_SOURCE_DATA
  );

  const emptyState =
    getDefaultRelationshipDiagramStateForWorkspaceMode(VIEW_ID);

  // combine global source streams with view-specific state here
  const reset$ = combineLatest([
    globalViewModelResetSource$,
    filteredViewSettings$,
  ]).pipe(
    map(([globalAppState, filteredSettingsWithChanges]) => ({
      ...globalAppState,
      viewSettings: filteredSettingsWithChanges.currentSettings,
    })),
    tap(() => {
      if (viewInstanceId) {
        dispatchAction(notifyViewLoading(viewInstanceId, { isBusy: true }));
      }
    })
  );

  const defaultViewInterface = getDefaultRelationshipDiagramsViewInterface();

  const viewModelSourceData$: Observable<SourceDataShape> = action$.pipe(
    withNamespace(viewInstanceId),
    combineReducers<
      RelationshipDiagramViewModelStreamState<RelationshipsViewSettings>
    >({ ...emptyState, viewInstanceId, viewInterface: defaultViewInterface }, [
      streamReducer(reset$, resetReducer),
      streamReducer(componentsChanged$, handleComponentsUpdate),
      streamReducer(scopeDiff$, setStateProperty('scopeDiffState')),
    ]),
    debounceTime(2 * DEFAULT_DEBOUNCE_TIME),
    tap(({ viewModel }) => {
      dispatchAction(
        readViewComponentIds({
          [VIEW_ID]: getComponentIdsFromViewModel(viewModel),
        })
      );
    })
  );

  const streamedViewProps$: Observable<
    RelationshipsViewStreamedProps<RelationshipsViewSettings> & {
      i18nLocale: I18nLocale;
      errorInfoBoxLabels: ErrorInfoBoxLabels;
      quickPerspectiveReferenceTypeFilters: Set<string> | null;
      referenceTypesForLegend: APIReferenceType[];
    }
  > = combineLatest([
    viewModelSourceData$,
    i18nLocale$,
    references$,
    models$,
  ]).pipe(
    map(([state, i18nLocale, references, models]) => {
      const users = orgUsersInterface.getAllOrgUsers();
      const viewProps = resetRelationshipsView(state);
      return {
        ...viewProps,
        viewInstanceId: state.viewInstanceId,
        users,
        i18nLocale,
        errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
        quickPerspectiveReferenceTypeFilters:
          filterInterface.getActiveQuickReferenceTypeFilters(),
        referenceTypesForLegend: getReferenceTypesForLegend(
          viewProps.viewModel.graphLinks,
          references,
          models
        ),
      };
    })
  );

  return withLiveIsLegendActive(streamedViewProps$, viewSettingsWithChanges$);
};
